/**
 * Content + Online store (T11-42, design/04 §10–11): the screens are built by T09
 * (`features/content`, `features/theme-editor`, `features/online-store`) — this module only mounts
 * them under the admin shell, gives each the access rule + title of its menu entry, and adds the
 * few read-only Online store pages the admin owns (markets, website addresses).
 *
 * | menu entry | route | screen | who |
 * |---|---|---|---|
 * | Pages · Policies · Menus · Media · Announcements · Redirects · Translations · Website texts | `/content/*` | T09 | `cms.edit` |
 * | Website design (themes) | `/online-store/themes` | T09 | `cms.edit` |
 * | Theme editor (full screen, no menu) | `/online-store/themes/:id/editor` | T09 in `EditorLayout` | `cms.edit` (publish: `cms.publish`, checked by T09) |
 * | Languages | `/online-store/languages` | T09-28 | `cms.edit` (changes: `cms.publish`) |
 * | Markets & currencies (read-only) | `/online-store/markets` | T11-45 `MarketsReadOnly` | `tax.read` |
 * | Website addresses (read-only) | `/online-store/domains` | `DomainsReadOnly` | `settings.read` |
 * | SEO | `/online-store/seo` | "being built" page (no cms API yet) | `cms.edit` |
 */
import type { RouteObject } from "react-router";

import type { AccessRule } from "@/auth/access";
import { contentRoutes } from "@/features/content/routes";
import { themeEditorFullScreenRoutes, themeEditorRoutes } from "@/features/theme-editor/routes";

import { allNavItems, type NavItem } from "../nav";
import type { RouteHandle } from "../layouts/AppLayout";

/**
 * Where the first-run checklist (T11-47) sends the owner for content work: the 特商法 page form
 * (T09-26, progress on Settings → Shop details) and the logo (theme settings in the editor).
 */
export const CONTENT_ONBOARDING_LINKS = {
  tokushoho: "/content/policies/tokushoho",
  shopDetails: "/settings/store",
  logo: "/online-store/themes/live/editor?page=index",
} as const;

/** Screens owned by T09 (theme editor + content) — mounted as they are, only given a handle. */
export const T09_ROUTES: readonly RouteObject[] = [...themeEditorRoutes, ...contentRoutes];

export function staticPrefix(path: string): string {
  const cut = path.search(/[:*]/);
  return `/${(cut >= 0 ? path.slice(0, cut) : path).replace(/^\/+|\/+$/g, "")}`;
}

/** The menu entry a route path belongs to (longest `to` that prefixes the route's static part). */
export function itemForPath(path: string): NavItem | undefined {
  const prefix = staticPrefix(path);
  return allNavItems()
    .filter((item) => item.to !== "/" && (prefix === item.to || prefix.startsWith(`${item.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

function withHandle(route: RouteObject, access?: AccessRule, titleKey?: string): RouteObject {
  const handle: RouteHandle = { ...(access ? { access } : {}), ...(titleKey ? { titleKey } : {}) };
  return {
    ...route,
    handle: { ...(route.handle as object | undefined), ...handle },
  } as RouteObject;
}

/** T09 route + the access rule of its menu entry (so the URL is guarded like the menu). */
export function mounted(route: RouteObject): RouteObject {
  if (!route.path) {
    return route;
  }
  const item = itemForPath(route.path);
  return item ? withHandle(route, item.access, `nav.items.${item.key}`) : route;
}

/** Online store pages of the admin itself (read-only in phase 1). */
const ONLINE_STORE_ROUTES: RouteObject[] = [
  {
    path: "online-store/markets",
    handle: {
      access: { permission: "tax.read" },
      titleKey: "nav.items.online_store_markets",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/settings/MarketsReadOnly")).MarketsReadOnly,
    }),
  },
  {
    path: "online-store/domains",
    handle: {
      access: { permission: "settings.read" },
      titleKey: "nav.items.online_store_domains",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/settings/DomainsReadOnly")).DomainsReadOnly,
    }),
  },
];

/** Inside the admin shell (sidebar / bottom bar). */
export const contentShellRoutes: readonly RouteObject[] = [
  ...T09_ROUTES.map(mounted),
  ...ONLINE_STORE_ROUTES,
];

/** Full screen without the admin menu (rendered in `EditorLayout`). */
export const editorFullScreenRoutes: readonly RouteObject[] =
  themeEditorFullScreenRoutes.map(mounted);

/** Menu addresses served above — they get no "being built" placeholder. */
export const contentPaths: ReadonlySet<string> = new Set(
  contentShellRoutes.map((route) => (route.path ? staticPrefix(route.path) : "")),
);
